"use client"

import * as React from "react"
import { RadioGroup as BaseRadioGroup } from "@base-ui/react/radio-group"
import { Radio } from "@base-ui/react/radio"
import { Circle } from "lucide-react"

import { cn } from "@/lib/utils"

type RadioSize = "sm" | "md" | "lg"

const RadioSizeContext = React.createContext<RadioSize>("md")

type RadioGroupProps = Omit<React.ComponentProps<typeof BaseRadioGroup>, "onValueChange"> & {
  onValueChange?: (value: string) => void
  size?: RadioSize
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, onValueChange, disabled, size = "md", ...props }, ref) => {
    return (
      <RadioSizeContext.Provider value={size}>
        <BaseRadioGroup
          ref={ref}
          className={cn(
            "grid gap-2",
            disabled && "opacity-50 pointer-events-none",
            className
          )}
          disabled={disabled}
          onValueChange={(value) => {
            onValueChange?.(String(value))
          }}
          {...props}
        />
      </RadioSizeContext.Provider>
    )
  }
)
RadioGroup.displayName = "RadioGroup"

const BOX_SIZE: Record<RadioSize, string> = {
  sm: "h-[14px] w-[14px]",
  md: "h-4 w-4",
  lg: "h-5 w-5",
}

const DOT_SIZE: Record<RadioSize, string> = {
  sm: "h-2 w-2",
  md: "h-2.5 w-2.5",
  lg: "h-3 w-3",
}

type RadioGroupItemProps = Omit<React.ComponentProps<typeof Radio.Root>, "ref"> & {
  value: string
  size?: RadioSize
}

const RadioGroupItem = React.forwardRef<HTMLSpanElement, RadioGroupItemProps>(
  ({ className, value, size, ...props }, ref) => {
    const ctx = React.useContext(RadioSizeContext)
    const resolved: RadioSize = size ?? ctx

    return (
      <Radio.Root
        ref={ref}
        value={value}
        className={cn(
          "aspect-square rounded-full border border-primary text-primary ring-offset-background",
          "transition-colors duration-[0.12s] ease-[cubic-bezier(0.4,0,0.2,1)]",
          BOX_SIZE[resolved],
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "flex items-center justify-center",
          "data-[state=checked]:bg-primary/10",
          className
        )}
        {...props}
      >
        <Radio.Indicator className="flex items-center justify-center">
          <Circle className={cn("fill-current text-current", DOT_SIZE[resolved])} />
        </Radio.Indicator>
      </Radio.Root>
    )
  }
)
RadioGroupItem.displayName = "RadioGroupItem"

export { RadioGroup, RadioGroupItem }
